import BaseAPI from './base.api';

export interface ManualDispatchExcavatorInput {
  excavatorBumper: string;
  gradeId: string;
  // one excavator, multiple dump zones (Backend Update #6) — backend round-robins
  // locked trucks across these by list position
  dumpingZoneIds: string[];
  lockedTrucks: string[];
  needsAllocation: boolean;
  capacity?: number;
}

export interface ManualDispatchTruckInput {
  bumperNumber: string;
  lat?: number;
  lon?: number;
  startLocationId?: string;
}

export interface ManualDispatchPlanPayload {
  planName?: string;
  status?: 'active' | 'draft';
  overridePlan?: boolean;
  totalTonnage?: number;
  excavators: ManualDispatchExcavatorInput[];
  trucks: ManualDispatchTruckInput[];
}

export interface ManualDispatchUpsertResponse {
  planId: string;
  status?: string;
}

interface ManualDispatchOverrideBase {
  truckBumper: string;
  assignmentTarget: 'current' | 'next';
  scope?: 'next_trip' | 'from_next_trip_onwards';
}

export interface ManualDispatchLoadingOverride
  extends ManualDispatchOverrideBase {
  nodeType: 'loading';
  excavatorBumper: string;
  // required by the backend for nodeType=loading — the grade the reassigned
  // truck should be loading, i.e. the target excavator's current gradeId
  gradeId: string;
}

export interface ManualDispatchDumpingOverride
  extends ManualDispatchOverrideBase {
  nodeType: 'dumping';
  dumpingZoneId: string;
}

export interface ManualDispatchParkingOverride
  extends ManualDispatchOverrideBase {
  nodeType: 'parking';
  parkingLocationId: string;
}

export type ManualDispatchOverridePayload =
  | ManualDispatchLoadingOverride
  | ManualDispatchDumpingOverride
  | ManualDispatchParkingOverride;

export interface ManualDispatchAssignment {
  truckBumper: string;
  excavatorBumper: string;
  loadingNode: string;
  dumpingNode: string;
  material: string;
  gradePercent: number;
  overrideNode: string | null;
  overrideNodeType: 'loading' | 'dumping' | 'parking' | null;
  overrideScope: string | null;
  // independent next-leg divert slot (Backend Update #10) — coexists with the
  // current-leg override slot above without clobbering it
  nextOverrideNode: string | null;
  nextOverrideNodeType: 'loading' | 'dumping' | 'parking' | null;
  nextOverrideScope: string | null;
  source: 'manual' | 'allocated';
  // "shovel" (default, matches excavator screen) or "locked" (a durable, explicit
  // lock — permanent until changed again, writes straight to loadingNode/dumpingNode).
  // Never "manual" — that's not a stored value; render the Manual badge whenever
  // hasActiveOverride is true, regardless of this field.
  assignmentType: 'shovel' | 'locked';
  // true whenever either override slot (current or next) is currently populated
  // (Backend Update #10) — the single source of truth for showing "Manual"
  hasActiveOverride: boolean;
}

// one row per excavator per plan, regardless of truck count — lets the excavator
// screen restore its dropdowns/checkboxes on reload even for zero-locked-truck excavators
export interface ManualDispatchExcavatorConfig {
  excavatorBumper: string;
  gradeId: string;
  dumpingZoneIds: string[];
  loadingNode: string;
  dumpingNodes: string[];
  material: string;
  gradePercent: number;
  capacity: number | null;
  needsAllocation: boolean;
}

export interface ManualDispatchActivePlanResponse {
  planId: string;
  planName?: string;
  status?: string;
  mode?: 'manual' | 'auto';
  assignments: ManualDispatchAssignment[];
  excavators: ManualDispatchExcavatorConfig[];
}

export interface ManualDispatchAddAssignmentPayload {
  truckBumper: string;
  excavatorBumper: string;
  gradeId: string;
  dumpingZoneId: string;
}

export interface ManualDispatchAddAssignmentResponse {
  planId: string;
  truckBumper: string;
  assignment: ManualDispatchAssignment;
}

// all fields optional — only send what actually changed for this excavator.
// lockedTrucks (when sent) is the FULL desired list for this excavator, not a delta —
// the backend diffs it against the excavator's current manual locks.
export interface ManualDispatchExcavatorPatchInput {
  gradeId?: string;
  // full desired list, not a delta (same semantics as lockedTrucks) — omit to
  // leave the excavator's dump zones unchanged
  dumpingZoneIds?: string[];
  lockedTrucks?: string[];
  needsAllocation?: boolean;
  capacity?: number;
  // solver candidate pool — only used when needsAllocation resolves true
  trucks?: ManualDispatchTruckInput[];
}

export interface ManualDispatchExcavatorPatchResponse {
  planId: string;
  excavatorBumper: string;
  excavator: ManualDispatchExcavatorConfig;
  assignments: ManualDispatchAssignment[];
}

// full-replace per excavator, same shape as POST /plan's excavators[] — resolves
// every listed truck's fate (refresh/insert/reassign, including swaps between two
// excavators in the same batch) from one snapshot in one transaction. All-or-nothing:
// a conflict fails the whole request with a single 409, never a partial application.
export interface ManualDispatchExcavatorsBatchPayload {
  excavators: ManualDispatchExcavatorInput[];
}

export interface ManualDispatchExcavatorsBatchResponse {
  planId: string;
  excavators: Array<{
    excavatorBumper: string;
    excavator: ManualDispatchExcavatorConfig;
    assignments: ManualDispatchAssignment[];
  }>;
}

// current/next-leg diverts reuse the exact discriminated nodeType + type-specific
// id field shape /override already validates — gradeId for loading, dumpingZoneId
// for dumping, parkingLocationId for parking. No excavatorBumper here — a loading
// divert resolves the excavator from gradeId server-side.
export interface ManualDispatchTruckLoadingDivert {
  nodeType: 'loading';
  gradeId: string;
}

export interface ManualDispatchTruckDumpingDivert {
  nodeType: 'dumping';
  dumpingZoneId: string;
}

export interface ManualDispatchTruckParkingDivert {
  nodeType: 'parking';
  parkingLocationId: string;
}

export type ManualDispatchTruckDivert =
  | ManualDispatchTruckLoadingDivert
  | ManualDispatchTruckDumpingDivert
  | ManualDispatchTruckParkingDivert;

// full atomic commit for one truck (Backend Update #10) — all fields optional,
// at least one must be present, only fields the dispatcher actually staged get
// sent. assignmentType/excavatorBumper/dumpingZoneId together are the durable
// shovel/locked binding; currentAssignment/nextAssignment are one-off diverts
// that never touch assignmentType or the durable binding.
export interface ManualDispatchTruckCommitInput {
  truckBumper: string;
  assignmentType?: 'shovel' | 'locked';
  excavatorBumper?: string;
  dumpingZoneId?: string;
  currentAssignment?: ManualDispatchTruckDivert;
  nextAssignment?: ManualDispatchTruckDivert;
}

export interface ManualDispatchTruckCommitPayload {
  assignments: ManualDispatchTruckCommitInput[];
}

export interface ManualDispatchTruckCommitResponse {
  planId: string;
  trucks: Array<{
    truckBumper: string;
    assignment: ManualDispatchAssignment;
  }>;
}

export default class ManualDispatchAPI extends BaseAPI {
  getActivePlan =
    async (): Promise<ManualDispatchActivePlanResponse | null> => {
      try {
        const response = await this.API.get('manual-dispatch/plan/active', {
          params: {
            customerId: this.customerId,
            // compression: false,
          },
        });
        return response.data;
      } catch (err: any) {
        if (err?.response?.status === 404) return null;
        throw err;
      }
    };

  upsertPlan = async (
    payload: ManualDispatchPlanPayload
  ): Promise<ManualDispatchUpsertResponse> => {
    const response = await this.API.post('manual-dispatch/plan', payload, {
      params: {
        customerId: this.customerId,
        // compression: false,
      },
    });
    return response.data;
  };

  patchExcavator = async (
    excavatorBumper: string,
    payload: ManualDispatchExcavatorPatchInput
  ): Promise<ManualDispatchExcavatorPatchResponse> => {
    const response = await this.API.patch(
      `manual-dispatch/plan/excavator/${excavatorBumper}`,
      payload,
      {
        params: {
          customerId: this.customerId,
          // , compression: false
        },
      }
    );
    return response.data;
  };

  patchExcavators = async (
    payload: ManualDispatchExcavatorsBatchPayload
  ): Promise<ManualDispatchExcavatorsBatchResponse> => {
    const response = await this.API.patch(
      'manual-dispatch/plan/excavators',
      payload,
      {
        params: {
          customerId: this.customerId,
          // compression: false,
        },
      }
    );
    return response.data;
  };

  // Hauler Monitor's one atomic per-truck commit — replaces the old pattern of
  // picking between addAssignment/override(loading)/override(dumping) per field
  patchTrucks = async (
    payload: ManualDispatchTruckCommitPayload
  ): Promise<ManualDispatchTruckCommitResponse> => {
    const response = await this.API.patch(
      'manual-dispatch/plan/trucks',
      payload,
      {
        params: {
          customerId: this.customerId,
          // , compression: false
        },
      }
    );
    return response.data;
  };

  override = async (payload: ManualDispatchOverridePayload): Promise<void> => {
    await this.API.post('manual-dispatch/override', payload, {
      params: {
        customerId: this.customerId,
        //  , compression: false
      },
    });
  };

  // inserts a brand-new assignment row for a truck that isn't in the active plan yet —
  // /override only works on trucks already in the plan
  addAssignment = async (
    payload: ManualDispatchAddAssignmentPayload
  ): Promise<ManualDispatchAddAssignmentResponse> => {
    const response = await this.API.post(
      'manual-dispatch/assignment',
      payload,
      {
        params: {
          customerId: this.customerId,
          //, compression: false
        },
      }
    );
    return response.data;
  };
}
